import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { addAppliedFilters, updateUriRef } from '../../actions/filterActions';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import get from 'lodash.get';
import { FindingRouter } from '../../router/FindingRouter';
import { searchBrowse as routes } from '../../router/routes/searchBrowse';
import { stripRoutingSegment, addCdnQueryParams } from '../../utils/uriUtils';
import { toggleRequestInFlight, batchAddAndApplyFilter } from '../../actions/filterActions';
import { getFollowTypeFromUri } from '../../utils/favoritesUtils';
import {
    hasPersonalizedFilterQueryParam,
    getPersonalizedFilterValues,
    getUrlWithoutPersonalizedFilterParam,
    getPersonalizedFilterAction,
    shouldApplyPersonalizedFilter,
} from '../../shared/helpers/personalizedFiltersHelpers';

export class SbSharedRefetchContainerComponent extends React.Component {
    constructor() {
        super();
        this.refetchCallback = this.refetchCallback.bind(this);
        this.personalizedFilterValues /*: string[] */ = [];
    }

    componentDidMount() {
        // set applied filters in reducer :
        const appliedFilters = this.props.searchBrowse.appliedFilters;
        this.props.addAppliedFilters(appliedFilters);

        this.initRouter();

        this.personalizedFilterValues = getPersonalizedFilterValues();
    }

    componentDidUpdate(prevProps) {
        const {
            regionalInfo,
            generatedUriRef,
            page: newPage,
            onSbRefetch,
            pageSize: newPageSize,
            searchBrowse,
            shippingFilters,
        } = this.props;
        /**
         * Make sure we have query params in both uri refs before comparing them to see if they've
         * changed.
         */
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const redirectTo = get(searchBrowse, 'spellCheck.redirect.redirectTo');
        if ((oldUriRef !== '' && newUriRef !== oldUriRef) || newPageSize !== prevProps.pageSize) {
            const variables = {
                // if uriRef changed because filter added/removed, reset to page 1
                page: newPage === prevProps.page ? 1 : newPage,
                uriRef: newUriRef,
                followSearchPages: [generatedUriRef],
                followSearchTypes: [getFollowTypeFromUri(generatedUriRef)],
                first: newPageSize,
            };
            // 1. set state to loading
            this.props.toggleRequestInFlight(true);

            // 2. do refetch
            onSbRefetch(variables, null, this.refetchCallback);

            // 3. update to new relay vars
            this.props.updateRelayVariables(variables);
        }

        if (redirectTo) {
            this.router.navigate(redirectTo);
        }

        const regionsByZipCode = get(regionalInfo, '[0].regionsByZipCode');
        if (this.personalizedFilterValues.length && regionsByZipCode) {
            for (const personalizedFilterValue of this.personalizedFilterValues) {
                if (shouldApplyPersonalizedFilter({ personalizedFilterValue, shippingFilters })) {
                    this.props.batchAddAndApplyFilter(
                        getPersonalizedFilterAction(personalizedFilterValue),
                        regionsByZipCode
                    );
                }
            }
        } else if (hasPersonalizedFilterQueryParam()) {
            const urlWithoutPersonalizedFilter = getUrlWithoutPersonalizedFilterParam();
            this.router.navigate(urlWithoutPersonalizedFilter);
        }
    }

    refetchCallback() {
        const displayUriRef = get(this.props, 'searchBrowse.displayUriRef');
        const newAppliedFilters = get(this.props, 'searchBrowse.appliedFilters');

        this.props.toggleRequestInFlight(false);

        this.props.addAppliedFilters(newAppliedFilters);

        if (displayUriRef) {
            this.router.navigate(displayUriRef);
        }
    }

    initRouter() {
        this.router = new FindingRouter({ routes });
        this.router.history.listen(({ pathname, search }, action) => {
            if (action === 'POP') {
                const uriRef = `${pathname}${search}`;
                this.props.updateUriRef(stripRoutingSegment(uriRef));
            }
        });
    }

    render() {
        return null;
    }
}

SbSharedRefetchContainerComponent.propTypes = {
    updateUriRef: PropTypes.func.isRequired,
    addAppliedFilters: PropTypes.func.isRequired,
    onSbRefetch: PropTypes.func.isRequired,
    searchBrowse: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    // TODO INFRA: Should be required
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    regionalInfo: PropTypes.array,
    batchAddAndApplyFilter: PropTypes.func.isRequired,
    shippingFilters: PropTypes.array,
};

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;
    const {
        variables: { first },
    } = relayVariables;
    return {
        page,
        generatedUriRef,
        pageSize: first,
    };
}

export const SbSharedRefetchContainer = createFragmentContainer(
    connect(
        mapStateToProps,
        {
            addAppliedFilters,
            updateUriRef,
            updateRelayVariables,
            toggleRequestInFlight,
            batchAddAndApplyFilter,
        }
    )(SbSharedRefetchContainerComponent),
    {
        searchBrowse: graphql`
            fragment SbSharedRefetchContainer_searchBrowse on SearchBrowseQueryConnection {
                displayUriRef
                spellCheck {
                    redirect {
                        redirectTo
                    }
                }
                appliedFilters {
                    name
                    rank
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
            }
        `,
        regionalInfo: graphql`
            fragment SbSharedRefetchContainer_regionalInfo on RegionalInfo @relay(plural: true) {
                regionsByZipCode {
                    displayName
                    urlLabel
                }
            }
        `,
        shippingFilters: graphql`
            fragment SbSharedRefetchContainer_shippingFilters on ShippingFilterType
                @relay(plural: true) {
                ...personalizedFiltersHelpers_shippingFilters @relay(mask: false)
            }
        `,
    }
);
